/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useMemo } from 'react'
import { useToast } from '@chakra-ui/react'
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useSearchState } from '@hooks/useSearchState'

import { baseStatsApiURL_NO_CACHE } from '@common/config'
import {
    ApiErrorMessage,
    EnrichedMatchEditorApiEvent,
    MatchEventEditorGetDataResponse,
    MatchEventEditorPostDataRequest,
    MatchEventEditorPostDataResponse,
    MatchEventEditorPutDataRequest,
    MatchEventEditorPutDataResponse,
    MatchEventEditorUnsupportedOperations,
    MatchEventPeriodNumber,
    MatchStatus,
    TMatchStatus,
} from '@common/types'

import { useUser } from '@queries/useUser'

import { useMatchLineup } from './useMatchLineup'
import { useTeamOverview } from './useTeamOverview'
import { useTeamSquad } from './useTeamSquad'

type SimpleTeamLineup = {
    id: string
    name: string
}

const endpoints = {
    get: (id: string) => `${baseStatsApiURL_NO_CACHE}/internal/editor/events?fixture_id=${id}`,
    create: (id: string) => `${baseStatsApiURL_NO_CACHE}/internal/editor/events/event?fixture_id=${id}`,
    update: (id: string, eventId: string) =>
        `${baseStatsApiURL_NO_CACHE}/internal/editor/events/event?fixture_id=${id}&event_id=${eventId}`,
    delete: (id: string, eventId: string) =>
        `${baseStatsApiURL_NO_CACHE}/internal/editor/events/event?fixture_id=${id}&event_id=${eventId}`,
}

async function handleResponse(response: Response) {
    if (response.status === 200) {
        return response.json()
    }

    const error = await response.json()

    return Promise.reject(new Error(error?.message ?? error?.error ?? '¯\\_(ツ)_/¯'))
}

async function fetchEventData(id?: string, accessToken?: string): Promise<MatchEventEditorGetDataResponse> {
    if (typeof id === 'undefined') {
        return Promise.reject(new Error('No match id provided'))
    }

    if (typeof accessToken === 'undefined') {
        return Promise.reject(new Error('No access token provided'))
    }

    const response = await fetch(endpoints.get(id), {
        headers: {
            Authentication: accessToken,
        },
    })

    return handleResponse(response)
}

async function createEventData(
    id?: string,
    data?: MatchEventEditorPostDataRequest,
    accessToken?: string,
): Promise<MatchEventEditorPostDataResponse> {
    if (typeof id === 'undefined') {
        return Promise.reject(new Error('No match id provided'))
    }

    if (typeof accessToken === 'undefined') {
        return Promise.reject(new Error('No access token provided'))
    }

    if (!data) {
        return Promise.reject(new Error('No match data provided'))
    }

    const response = await fetch(endpoints.create(id), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authentication: accessToken,
        },
        body: JSON.stringify(data),
    })

    return handleResponse(response)
}

async function updateEventData(
    id?: string,
    eventId?: string,
    data?: MatchEventEditorPutDataRequest,
    accessToken?: string,
): Promise<MatchEventEditorPutDataResponse> {
    if (typeof id === 'undefined') {
        return Promise.reject(new Error('No match id provided'))
    }

    if (typeof eventId === 'undefined') {
        return Promise.reject(new Error('No event id provided'))
    }

    if (typeof accessToken === 'undefined') {
        return Promise.reject(new Error('No access token provided'))
    }

    if (!data) {
        return Promise.reject(new Error('No event data provided'))
    }

    const response = await fetch(endpoints.update(id, eventId), {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authentication: accessToken,
        },
        body: JSON.stringify(data),
    })

    return handleResponse(response)
}

async function deleteEventData(
    id?: string,
    eventId?: string,
    accessToken?: string,
): Promise<MatchEventEditorPutDataResponse> {
    if (typeof id === 'undefined') {
        return Promise.reject(new Error('No match id provided'))
    }

    if (typeof eventId === 'undefined') {
        return Promise.reject(new Error('No event id provided'))
    }

    if (typeof accessToken === 'undefined') {
        return Promise.reject(new Error('No access token provided'))
    }

    const response = await fetch(endpoints.delete(id, eventId), {
        method: 'DELETE',
        headers: {
            Authentication: accessToken,
        },
    })

    return handleResponse(response)
}

async function deleteEventWithRetry(id: string, eventId: string, accessToken: string) {
    try {
        return await deleteEventData(id, eventId, accessToken)
    } catch (firstError) {
        try {
            return await deleteEventData(id, eventId, accessToken)
        } catch {
            throw firstError
        }
    }
}

function isMatchLive(status?: TMatchStatus): boolean {
    if (typeof status === 'undefined') {
        return false
    }

    const liveStatuses = [
        MatchStatus.started,
        MatchStatus.a1st_half,
        MatchStatus.a2nd_half,
        MatchStatus.halftime,
        MatchStatus.awaiting_extra_time,
        MatchStatus.extra_time_halftime,
        MatchStatus.awaiting_penalties,
        MatchStatus.a1st_extra,
        MatchStatus.a2nd_extra,
        MatchStatus.penalties,
        MatchStatus.overtime,
    ].map(matchStatus => Number(matchStatus))

    return liveStatuses.includes(status)
}

// TODO: remove this function and its usages, causes slowdown
// query should be updated with response not invalidated with delay
function delayInvalidation(delay) {
    return new Promise(resolve => {
        setTimeout(resolve, delay)
    })
}

const eventQueryKeys = {
    all: ['events'] as const,
    single: (id: number | string | null) => [...eventQueryKeys.all, String(id)] as const,
    deleteAllProgress: (id: number | string | null) => ['deleteAllEventsProgress', String(id)] as const,
}

const eventMutationKeys = {
    remove: (id: number | string | null) => ['removeEvent', String(id)] as const,
    removeAll: (id: number | string | null) => ['removeAllEvents', String(id)] as const,
}

type DeleteAllProgress = { completed: number; total: number }

export function useMatchEventEditorData() {
    const { userData } = useUser()

    const queryClient = useQueryClient()
    const toast = useToast()

    const [{ matchId: selectedMatchId }] = useSearchState()

    // Shared through the query cache so every hook instance sees the same batch progress
    const { data: deleteAllProgress = null } = useQuery<DeleteAllProgress | null>({
        queryKey: eventQueryKeys.deleteAllProgress(selectedMatchId),
        queryFn: () => null,
        enabled: false,
        staleTime: Infinity,
    })

    const setDeleteAllProgress = (progress: DeleteAllProgress | null) =>
        queryClient.setQueryData(eventQueryKeys.deleteAllProgress(selectedMatchId), progress)

    const {
        data: eventData,
        isLoading: isLoadingEvent,
        isError,
        error: eventDataError,
        refetch: refetchEventData,
    } = useQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: eventQueryKeys.single(selectedMatchId),
        queryFn: async () => fetchEventData(String(selectedMatchId), userData?.accessToken),
        enabled: !!selectedMatchId && !!userData?.accessToken,
        refetchInterval(query) {
            return isMatchLive(query.state.data?.status) ? 5000 : false
        },
    })

    const { data: team1OverviewData, isLoading: isLoadingTeam1Overview } = useTeamOverview(eventData?.team1_id)
    const { data: team2OverviewData, isLoading: isLoadingTeam2Overview } = useTeamOverview(eventData?.team2_id)
    const { data: matchLineups, isLoading: isLoadingLineups } = useMatchLineup(selectedMatchId)
    const { data: team1Squad, isLoading: isLoadingSquad1 } = useTeamSquad(eventData?.team1_id, {
        enabled: !!eventData?.team1_id && matchLineups?.team1Lineup?.length === 0,
    })

    const { data: team2Squad, isLoading: isLoadingSquad2 } = useTeamSquad(eventData?.team2_id, {
        enabled: !!eventData?.team2_id && matchLineups?.team1Lineup?.length === 0,
    })

    const isLoadingSquads = isLoadingSquad1 || isLoadingSquad2
    const isLoadingOverviews = isLoadingTeam1Overview || isLoadingTeam2Overview

    const events = useMemo<EnrichedMatchEditorApiEvent[]>(() => {
        if (isLoadingOverviews || !eventData) {
            return []
        }

        return [...eventData.events].reverse().map(event => ({
            ...event,
            // TODO: Ask backend to consolidate this, 6 is coming on match finish event sometimes
            period_number: event.period_number === 6 ? MatchEventPeriodNumber.second_half : event.period_number,
            team_name:
                event.team_id === eventData?.team1_id ? team1OverviewData?.team?.name : team2OverviewData?.team?.name,
            isEditable: !event.unsupported_operations.includes(MatchEventEditorUnsupportedOperations.EDIT),
            isRemovable: !event.unsupported_operations.includes(MatchEventEditorUnsupportedOperations.DELETE),
        }))
    }, [eventData, team1OverviewData, team2OverviewData, isLoadingOverviews])

    const data = useMemo(
        () => ({
            ...eventData,
            team1_info: team1OverviewData?.team,
            team2_info: team2OverviewData?.team,
        }),
        [eventData, team1OverviewData, team2OverviewData],
    )

    const {
        mutateAsync: create,
        isPending: isCreating,
        isSuccess: isCreated,
    } = useMutation({
        mutationFn: async (createData: MatchEventEditorPostDataRequest) =>
            createEventData(String(selectedMatchId), createData, userData?.accessToken),
        onSettled: async () => {
            await delayInvalidation(2000)

            queryClient.invalidateQueries({ queryKey: eventQueryKeys.single(selectedMatchId) })
        },
        onSuccess: () => {
            toast({
                position: 'top',
                title: 'Event created',
                status: 'success',
            })
        },
        onError: error => {
            if (error.message !== ApiErrorMessage.UNAUTHORIZED) {
                toast({
                    position: 'top',
                    description: `Error creating event: ${error.message}`,
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                })
            }
        },
    })

    const {
        mutateAsync: update,
        isPending: isUpdating,
        isSuccess: isUpdated,
    } = useMutation({
        mutationFn: async (updateData: MatchEventEditorPutDataRequest) =>
            updateEventData(String(selectedMatchId), updateData?.id, updateData, userData?.accessToken),
        onSettled: async () => {
            await delayInvalidation(2000)

            queryClient.invalidateQueries({ queryKey: eventQueryKeys.single(selectedMatchId) })
        },
        onSuccess: () => {
            toast({
                position: 'top',
                title: 'Event updated',
                status: 'success',
            })
        },
        onError: error => {
            if (error.message === ApiErrorMessage.UNAUTHORIZED) {
                toast({
                    position: 'top',
                    description: `Error updating event: ${error.message}`,
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                })
            }
        },
    })

    const { mutateAsync: remove, isSuccess: isRemoved } = useMutation({
        mutationKey: eventMutationKeys.remove(selectedMatchId),
        mutationFn: async (eventId: number) =>
            deleteEventData(String(selectedMatchId), String(eventId), userData?.accessToken),
        onSettled: async () => {
            await delayInvalidation(2000)

            return queryClient.invalidateQueries({ queryKey: eventQueryKeys.single(selectedMatchId) })
        },
        onSuccess: () => {
            toast({
                position: 'top',
                title: 'Event deleted',
                status: 'success',
            })
        },
        onError: error => {
            if (error.message !== ApiErrorMessage.UNAUTHORIZED) {
                toast({
                    position: 'top',
                    description: `Error deleting event: ${error.message}`,
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                })
            }
        },
    })

    const { mutateAsync: removeAll } = useMutation({
        mutationKey: eventMutationKeys.removeAll(selectedMatchId),
        mutationFn: async (eventIds: number[]) => {
            const fixtureId = String(selectedMatchId)
            const accessToken = userData?.accessToken

            if (!accessToken) {
                throw new Error('No access token provided')
            }

            setDeleteAllProgress({ completed: 0, total: eventIds.length })

            for (let index = 0; index < eventIds.length; index += 1) {
                // Sequential on purpose: stop at the first failure and report progress per event
                // eslint-disable-next-line no-await-in-loop
                await deleteEventWithRetry(fixtureId, String(eventIds[index]), accessToken)

                setDeleteAllProgress({ completed: index + 1, total: eventIds.length })
            }
        },
        onSettled: async () => {
            setDeleteAllProgress(null)

            await delayInvalidation(2000)

            return queryClient.invalidateQueries({ queryKey: eventQueryKeys.single(selectedMatchId) })
        },
        onSuccess: (_data, eventIds) => {
            toast({
                position: 'top',
                title: 'Events deleted',
                description: `${eventIds.length} event${eventIds.length === 1 ? '' : 's'} removed.`,
                status: 'success',
            })
        },
        onError: error => {
            if (error.message !== ApiErrorMessage.UNAUTHORIZED) {
                toast({
                    position: 'top',
                    title: 'Delete all stopped',
                    description: `${error.message} Some events may already be removed. Use Delete all events again and confirm to continue.`,
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                })
            }
        },
    })

    const isRemoving = useIsMutating({ mutationKey: eventMutationKeys.remove(selectedMatchId) }) > 0
    const isRemovingAll = useIsMutating({ mutationKey: eventMutationKeys.removeAll(selectedMatchId) }) > 0

    const lineups = useMemo<Record<string, SimpleTeamLineup[] | undefined>>(() => {
        if (
            !isLoadingLineups &&
            !isLoadingEvent &&
            matchLineups?.team1Lineup?.length &&
            matchLineups?.team2Lineup?.length &&
            eventData?.team1_id &&
            eventData?.team2_id
        ) {
            return {
                [eventData?.team1_id]: matchLineups.team1Lineup
                    .map(({ player_id, name, substitute }) => ({ id: player_id, name, substitute }))
                    // eslint-disable-next-line no-nested-ternary
                    .sort((a, b) => (a.substitute === b.substitute ? 0 : a.substitute ? 1 : -1)),
                [eventData?.team2_id]: matchLineups.team2Lineup
                    .map(({ player_id, name, substitute }) => ({ id: player_id, name, substitute }))
                    // eslint-disable-next-line no-nested-ternary
                    .sort((a, b) => (a.substitute === b.substitute ? 0 : a.substitute ? 1 : -1)),
            }
        }

        if (isLoadingSquads) {
            return {}
        }

        return {
            [`${eventData?.team1_id}`]: team1Squad,
            [`${eventData?.team2_id}`]: team2Squad,
        }
    }, [
        matchLineups,
        team1Squad,
        team2Squad,
        eventData?.team1_id,
        eventData?.team2_id,
        isLoadingSquads,
        isLoadingLineups,
        isLoadingEvent,
    ])

    useEffect(() => {
        if (isError) {
            toast({
                position: 'top',
                description: `Error fetching match events: ${eventDataError.message}`,
                status: 'error',
                duration: 9000,
                isClosable: true,
            })
        }
    }, [isError, eventDataError, toast])

    return {
        data,
        events,
        lineups,
        isLoading: isLoadingEvent,
        isError,
        create,
        isCreating,
        isCreated,
        update,
        isUpdating,
        isUpdated,
        remove,
        isRemoving,
        isRemoved,
        removeAll,
        isRemovingAll,
        deleteAllProgress,
        refetchEventData,
    }
}
